import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { ReactNode } from "react";
import type { GlossaryTerm, MonthlyMetric } from "../types";
import { yen } from "../lib/format";
import { HelpButton } from "./HelpButton";

const axisStyle = { fill: "var(--text-muted)", fontSize: 11 };

type ChartFrameProps = {
  title: string;
  description: string;
  term?: GlossaryTerm;
  onHelp: (term: GlossaryTerm) => void;
  children: ReactNode;
  wide?: boolean;
};

export function ChartFrame({ title, description, term, onHelp, children, wide }: ChartFrameProps) {
  return (
    <section className={`chart-frame${wide ? " chart-frame--wide" : ""}`}>
      <header>
        <div>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
        <HelpButton term={term} onOpen={onHelp} />
      </header>
      <div className="chart-frame__canvas">{children}</div>
    </section>
  );
}

export function RevenueChart({ data }: { data: MonthlyMetric[] }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="revenueFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#356fae" stopOpacity={0.24} />
            <stop offset="100%" stopColor="#356fae" stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} stroke="var(--chart-grid)" />
        <XAxis dataKey="month" tickFormatter={(v) => `${v}月`} tick={axisStyle} axisLine={false} tickLine={false} />
        <YAxis tickFormatter={(v) => `${Math.round(v / 1_000_000)}M`} tick={axisStyle} axisLine={false} tickLine={false} width={38} />
        <Tooltip formatter={(value) => yen(Number(value))} labelFormatter={(label) => `${label}月`} contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12 }} />
        <Area type="monotone" dataKey="revenue" name="売上高" stroke="#356fae" fill="url(#revenueFill)" strokeWidth={2.5} />
        <Line type="monotone" dataKey="operatingProfit" name="営業利益" stroke="#2f7d62" strokeWidth={2} dot={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export function CustomerChart({ data }: { data: MonthlyMetric[] }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid vertical={false} stroke="var(--chart-grid)" />
        <XAxis dataKey="month" tickFormatter={(v) => `${v}月`} tick={axisStyle} axisLine={false} tickLine={false} />
        <YAxis tick={axisStyle} axisLine={false} tickLine={false} width={32} />
        <Tooltip formatter={(value) => `${Number(value).toFixed(1)}件`} labelFormatter={(label) => `${label}月`} contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12 }} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Line type="monotone" dataKey="b2bActive" name="BtoB稼働" stroke="#356fae" strokeWidth={2.3} dot={false} />
        <Line type="monotone" dataKey="b2cActive" name="BtoC稼働" stroke="#7a5da9" strokeWidth={2.3} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

export function AcquisitionChart({ data }: { data: MonthlyMetric[] }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 10, right: 8, left: 0, bottom: 0 }} barGap={2}>
        <CartesianGrid vertical={false} stroke="var(--chart-grid)" />
        <XAxis dataKey="month" tickFormatter={(v) => `${v}月`} tick={axisStyle} axisLine={false} tickLine={false} />
        <YAxis tick={axisStyle} axisLine={false} tickLine={false} width={32} />
        <Tooltip formatter={(value) => `${Number(value).toFixed(1)}件`} labelFormatter={(label) => `${label}月`} contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12 }} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Bar dataKey="b2bNew" name="BtoB新規" fill="#356fae" radius={[4, 4, 0, 0]} />
        <Bar dataKey="b2cNew" name="BtoC新規" fill="#7a5da9" radius={[4, 4, 0, 0]} />
        <Bar dataKey="b2bChurn" name="BtoB解約" fill="#d9896a" radius={[4, 4, 0, 0]} />
        <Bar dataKey="b2cChurn" name="BtoC解約" fill="#c6a052" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}
